import { CalendarDays, Compass, MapPinned, Plane, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { rankVisits } from '../domain/aggregators';
import { confidenceLabel } from '../domain/confidence';
import { formatKm } from '../domain/distance';
import type { AnalysisResult, DailySummary, TimelineVisit } from '../domain/models';

export function JourneyNarrative({ result }: { result: AnalysisResult }) {
  const topVisits = rankVisits(result.visits, 4);
  const landmarkDays = selectLandmarkDays(result.dailySummaries);
  const firstDate = result.stats.dateStart ?? '-';
  const lastDate = result.stats.dateEnd ?? '-';
  const travelDays = result.dailySummaries.filter((day) => day.tags.includes('travel')).length;
  const averageDistance = result.dailySummaries.length > 0 ? result.stats.totalDistanceMeters / result.dailySummaries.length : 0;

  return (
    <section className="narrative-hero surface">
      <div className="narrative-kicker">
        <Sparkles size={17} aria-hidden="true" />
        Timeline Story
      </div>
      <h2>{firstDate} から {lastDate} までの移動ログ</h2>
      <p className="narrative-lead">
        この期間は {result.dailySummaries.length.toLocaleString('ja-JP')} 日分の記録から、約 {formatKm(result.stats.totalDistanceMeters)} km の移動と
        {result.visits.length.toLocaleString('ja-JP')} 件の滞在候補が見つかりました。まずは「よく滞在した場所」と「移動が大きい日」を手がかりに読むと、生活圏、旅行、仕事や用事の山が見えてきます。
      </p>

      <div className="story-chapters">
        <StoryChapter
          icon={<Compass size={18} aria-hidden="true" />}
          title="移動のリズム"
          body={`1日平均は約 ${formatKm(averageDistance)} km。移動距離が突出する日は、旅行・出張・遠出の候補として確認する価値があります。`}
        />
        <StoryChapter
          icon={<MapPinned size={18} aria-hidden="true" />}
          title="滞在の核"
          body={topVisits.length > 0 ? `長く滞在した候補は ${topVisits.map(formatVisitName).join('、')} です。` : '滞在候補が少ないため、生活圏の推定はまだ弱めです。'}
        />
        <StoryChapter
          icon={<Plane size={18} aria-hidden="true" />}
          title="非日常の候補"
          body={result.trips.length > 0 ? `${result.trips.length} 件の旅行/遠征候補があります。Trip Candidates で期間と理由を確認できます。` : `${travelDays} 日が長距離移動として目立ちます。旅行候補としてはまだ弱めです。`}
        />
      </div>

      {landmarkDays.length > 0 ? (
        <div className="story-days">
          <div>
            <CalendarDays size={18} aria-hidden="true" />
            <strong>読み始めに向いている日</strong>
          </div>
          {landmarkDays.map((day) => (
            <article key={day.date}>
              <span>{day.date}</span>
              <p>{describeDay(day)}</p>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function StoryChapter({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <article className="story-chapter">
      <div>{icon}</div>
      <strong>{title}</strong>
      <p>{body}</p>
    </article>
  );
}

function selectLandmarkDays(days: DailySummary[]): DailySummary[] {
  const byDistance = [...days].sort((a, b) => b.distanceMeters - a.distanceMeters).slice(0, 2);
  const byVisits = [...days]
    .filter((day) => !byDistance.some((selected) => selected.date === day.date))
    .sort((a, b) => b.visitCount - a.visitCount)
    .slice(0, 1);
  return [...byDistance, ...byVisits].filter((day) => day.distanceMeters > 0 || day.visitCount > 0);
}

function describeDay(day: DailySummary): string {
  const fragments = [
    `推定移動距離 ${formatKm(day.distanceMeters)} km`,
    `訪問候補 ${day.visitCount} 件`,
    `確度 ${confidenceLabel(day.confidence)}`,
  ];
  if (day.tags.includes('travel')) fragments.push('遠出の可能性');
  if (day.tags.includes('event_day')) fragments.push('予定が多い日');
  return fragments.join(' / ');
}

function formatVisitName(visit: TimelineVisit): string {
  return visit.placeName ?? `${visit.center.lat.toFixed(3)}, ${visit.center.lng.toFixed(3)} 周辺`;
}
